import type { SourceMeta } from '../types';
import { computePeaks, detectTempo, detectTransients, onsetEnvelope, toMono } from './analysis';
import { audioCtx } from './context';
import { putSample } from './registry';
import { buildSliceEdges, inferBars } from './slice';

const PALETTE = ['#ff2d95', '#38bdf8', '#fbbf24', '#5eead4', '#a78bfa', '#fb7185'];

let colorCursor = 0;
export function nextColor(): string {
  return PALETTE[colorCursor++ % PALETTE.length];
}

export function uid(prefix = 'id'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function snapToNearest(t: number, candidates: number[], tolerance: number): number {
  let best = t;
  let dist = tolerance;
  for (const c of candidates) {
    const d = Math.abs(c - t);
    if (d < dist) {
      dist = d;
      best = c;
    }
  }
  return best;
}

export interface ImportResult {
  meta: SourceMeta;
  buffer: AudioBuffer;
}

/**
 * Auto-gain, so imported audio sits where the built-in kits sit.
 *
 * The kits are peak-limited to 0.92 when rendered, which makes them uniformly
 * hot. Real recordings are not: measuring the shipped CC0 pack found a 17 dB
 * spread in RMS while most files already peaked at full scale — so peak
 * normalising would have changed nothing at all. Loudness is the only thing
 * worth matching here.
 *
 * This returns a gain rather than rewriting samples: the source keeps its
 * original audio, the value lands in `SourceMeta.gain`, and the sequencer
 * applies it per step. That leaves it visible and adjustable instead of baked
 * in.
 */
const TARGET_RMS = 0.2; // ≈ -14 dBFS, the median of real drum loops
const PEAK_CEILING = 0.92; // same ceiling renderKit() limits the kits to
const MAX_BOOST = 4; // +12 dB, so near-silent files don't drag up their noise

export function loudnessGain(mono: Float32Array, buffer: AudioBuffer): number {
  let sum = 0;
  for (let i = 0; i < mono.length; i++) sum += mono[i] * mono[i];
  const rms = Math.sqrt(sum / Math.max(1, mono.length));
  if (!Number.isFinite(rms) || rms <= 1e-6) return 1; // silence — leave it alone

  // Peak across every channel: clipping happens per channel, not in the mixdown.
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < data.length; i++) {
      const v = Math.abs(data[i]);
      if (v > peak) peak = v;
    }
  }

  let gain = TARGET_RMS / rms;
  gain = Math.min(gain, MAX_BOOST);
  if (peak > 0) gain = Math.min(gain, PEAK_CEILING / peak); // never clip
  return Math.round(Math.max(0.05, gain) * 1000) / 1000;
}

/** Decodes a File, runs analysis, and registers the sample data. */
export async function importAudioFile(file: File, beatsPerBar: number): Promise<ImportResult> {
  const bytes = await file.arrayBuffer();
  const buffer = await audioCtx().decodeAudioData(bytes);
  return registerBuffer(buffer, file.name.replace(/\.[^.]+$/, ''), beatsPerBar);
}

/** Shared by file import and track bouncing. */
export function registerBuffer(
  buffer: AudioBuffer,
  name: string,
  beatsPerBar: number,
  opts: { bpm?: number; bounced?: boolean } = {},
): ImportResult {
  const mono = toMono(buffer);
  const env = onsetEnvelope(mono, buffer.sampleRate);
  const detected = detectTempo(env, { beatsPerBar, duration: buffer.duration });
  const bpm = opts.bpm ?? Math.round(detected.bpm * 100) / 100;
  const transients = detectTransients(env);
  // The envelope is only accurate to a frame (~6 ms); a nearby transient is a
  // better downbeat than the frame centre.
  const offset =
    opts.bpm !== undefined
      ? 0
      : Math.max(0, snapToNearest(Math.max(0, detected.offset), transients, 0.04));

  const meta: SourceMeta = {
    id: uid('src'),
    name,
    duration: buffer.duration,
    sampleRate: buffer.sampleRate,
    channels: buffer.numberOfChannels,
    bpm,
    bpmConfidence: detected.confidence,
    offset,
    bars: inferBars(buffer.duration, bpm, beatsPerBar),
    transients,
    division: 4,
    sliceEdges: [],
    snapToTransients: false,
    rootPitch: 0,
    // A bounce is already sitting at the level you mixed it to — only imported
    // audio gets matched to the kits.
    gain: opts.bounced ? 1 : loudnessGain(mono, buffer),
    color: nextColor(),
    bounced: opts.bounced,
  };
  meta.sliceEdges = buildSliceEdges(meta, beatsPerBar);

  putSample(meta.id, { buffer, peaks: computePeaks(buffer) });
  return { meta, buffer };
}
